import { useEffect, useState } from "react";
import "../styles/animations.css";

const ACCENT = "#2DD4BF"; // teal-400

/** Core capabilities shown as chips. */
const capabilities = [
  "Enterprise AV system design",
  "Web-conferencing room design",
  "Microsoft Teams & Skype Rooms",
  "Surface Hub deployment",
  "Smart-home automation",
  "Crestron programming",
  "Savant programming",
  "Touch-panel UI design",
  "Device driver development",
  "Lighting · HVAC · alarm integration",
  "RFP & bid analysis",
  "ROM estimating",
  "Project management",
];

/** Scope stats for the featured Microsoft engagement. */
const engagementStats: { label: string; value: string }[] = [
  { label: "Client", value: "Microsoft" },
  { label: "Engaged via", value: "Arup" },
  { label: "Regions", value: "US · Canada · EU" },
  { label: "Rooms", value: "Huddle → boardroom" },
];

/**
 * Portfolio photos — a tour of Microsoft meeting spaces, each framed as
 * "use case → the solution I recommended." Wide shots are panoramas that
 * span the full gallery width; the rest sit two-up.
 * Files live in `public/assets/av-portfolio/`.
 */
type Shot = {
  src: string;
  title: string;
  caption: string;
  wide?: boolean;
  position?: string;
};

const gallery: Shot[] = [
  {
    src: "/assets/av-portfolio/executive-boardroom.jpg",
    title: "Executive boardroom",
    caption:
      "Ceiling projection for the whole room, plus a wall-mounted Surface Hub for inking and remote presenters.",
    wide: true,
  },
  {
    src: "/assets/av-portfolio/conference-room.jpg",
    title: "Conference room",
    caption:
      "One Surface Hub as an all-in-one Skype Room System — camera, mics, and display in a single install.",
  },
  {
    src: "/assets/av-portfolio/toronto-boardroom.jpg",
    title: "Boardroom · Toronto",
    caption:
      "An 18-seat room sized for a single large display with a touch console on the table.",
  },
  {
    src: "/assets/av-portfolio/collaboration-lounge.jpg",
    title: "Collaboration lounge",
    caption:
      "Twin Surface Hubs and a shared display turn an open lounge into flexible breakout space.",
    wide: true,
  },
  {
    src: "/assets/av-portfolio/focus-room.jpg",
    title: "Private focus room",
    caption:
      "An 84″ Surface Hub for whiteboarding and one-to-one calls, where a projector would be overkill.",
  },
  {
    src: "/assets/av-portfolio/knowledge-center.jpg",
    title: "Knowledge center",
    caption:
      "An overhead Surface Hub mirrored to a kiosk screen for walk-up briefings and demos.",
    position: "center 40%",
  },
  {
    src: "/assets/av-portfolio/experience-center.jpg",
    title: "Experience center",
    caption:
      "Themed vignettes each running the platform, so visitors see the technology in their own context.",
    wide: true,
  },
  {
    src: "/assets/av-portfolio/training-room.jpg",
    title: "Flexible training room",
    caption:
      "A ceiling projector and large display with reconfigurable tables for class or workshop layouts.",
  },
  {
    src: "/assets/av-portfolio/presentation-room.jpg",
    title: "Presentation room",
    caption:
      "A rolling Surface Hub paired with a lectern and projector for larger, podium-style sessions.",
  },
  {
    src: "/assets/av-portfolio/huddle-room.jpg",
    title: "Huddle room",
    caption:
      "Dual displays for multi-site video, driven by a wall keypad simple enough for anyone to start a call.",
    wide: true,
  },
];

/**
 * Luxury residential work — private cinema and whole-home automation.
 * Client identities are deliberately withheld; these are private homes.
 * Same wide/normal layout rules as the enterprise gallery.
 */
const residentialGallery: Shot[] = [
  {
    src: "/assets/av-portfolio/residential/private-cinema.jpg",
    title: "Private cinema",
    caption:
      "A dedicated screening room with film-grade projection, acoustic treatment, and one-touch control of picture, sound, and lighting.",
  },
  {
    src: "/assets/av-portfolio/residential/modern-art-home.jpg",
    title: "Modern art home",
    caption:
      "Automation tucked invisibly into the architecture — lighting scenes, shades, and multi-room audio on a single interface.",
  },
  {
    src: "/assets/av-portfolio/residential/formal-living-room.jpg",
    title: "Formal living room",
    caption:
      "Whole-home control integrated without disturbing the room's design — lighting, climate, shades, and audio, all concealed.",
    wide: true,
  },
  {
    src: "/assets/av-portfolio/residential/waterfront-glass.jpg",
    title: "Waterfront glass house",
    caption:
      "A room that's mostly window — motorized shades, lighting, and audio balanced so the view always wins.",
  },
  {
    src: "/assets/av-portfolio/residential/reception-room.jpg",
    title: "Reception room",
    caption:
      "Concealed speakers and preset lighting scenes turn a formal space into an effortless room for entertaining.",
  },
  {
    src: "/assets/av-portfolio/residential/water-wall-estate.jpg",
    title: "Estate with water features",
    caption:
      "Indoor and outdoor audio, lighting, and water features all run from one control system, inside and out.",
    wide: true,
  },
  {
    src: "/assets/av-portfolio/residential/coastal-room.jpg",
    title: "Coastal estate",
    caption:
      "A bluff-top room on the California coast, with motorized shades and distributed audio tuned to the setting.",
  },
  {
    src: "/assets/av-portfolio/residential/mediterranean-estate.jpg",
    title: "Mediterranean estate",
    caption:
      "A hillside estate wired end to end for lighting, security, and whole-property audio.",
  },
  {
    src: "/assets/av-portfolio/residential/curved-estate.jpg",
    title: "Multi-level estate",
    caption:
      "Automation reaching every room, terrace, and garden across multiple levels of the home.",
    wide: true,
  },
  {
    src: "/assets/av-portfolio/residential/pool-estate.jpg",
    title: "Wired from construction",
    caption:
      "Conduit, equipment racks, and control wiring designed in during the build — the right way to wire a large home.",
  },
  {
    src: "/assets/av-portfolio/residential/garden-residence.jpg",
    title: "Garden residence",
    caption:
      "Distributed audio and lighting extending from the interior out across the grounds.",
  },
];

type Experience = {
  company: string;
  role: string;
  location: string;
  date: string;
  summary: string;
  clients?: string[];
};

/** AV / automation roles, pulled from the résumé. */
const experience: Experience[] = [
  {
    company: "Arup",
    role: "Audiovisual & IT Consultant",
    location: "Los Angeles / Toronto",
    date: "2016 – 2020",
    summary:
      "Owner's representative for enterprise-level AV systems across commercial buildings and transportation — concept presentations to stakeholders, ROM estimates, RFP documents and bid analysis, custom AV design, master planning, and construction administration.",
    clients: [
      "Microsoft",
      "Kaiser Permanente",
      "USC",
      "Toronto Pearson Airport",
      "WeWork",
    ],
  },
  {
    company: "DSI Luxury Technology",
    role: "Crestron Integration Programmer",
    location: "Los Angeles",
    date: "2014 – 2016",
    summary:
      "Residential automation control programming for Crestron and Savant projects — touch-panel interface design, device driver development across AV, lighting, alarm and HVAC, and client consultations for custom software.",
  },
  {
    company: "C3 — Custom Code Crafters",
    role: "Project Manager / Programmer",
    location: "Essex / London, UK",
    date: "2009 – 2013",
    summary:
      "Residential automation control programming for Crestron and Savant systems — touch-panel UI design, device drivers for AV, lighting, alarm and HVAC, and client consulting on bespoke installations.",
  },
  {
    company: "Visual Defence / PJS Systems",
    role: "Crestron Programmer / Project Lead",
    location: "Burnaby, BC",
    date: "2008 – 2009",
    summary:
      "Automation control software for corporate and government facilities — touch-panel interface design, device driver development, and client consultations for custom software programming.",
  },
];

function GalleryFigure({ shot }: { shot: Shot }) {
  return (
    <figure
      className={`group relative overflow-hidden rounded-xl border border-white/10 bg-black/30 ${
        shot.wide ? "md:col-span-2" : ""
      }`}
    >
      <img
        src={shot.src}
        alt={`${shot.title} — ${shot.caption}`}
        loading="lazy"
        className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
          shot.wide ? "aspect-[21/9]" : "aspect-[4/3]"
        }`}
        style={shot.position ? { objectPosition: shot.position } : undefined}
      />
      {/* Always-on gradient so the caption stays legible; deepens on hover. */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent transition-opacity duration-300 group-hover:from-black/90" />
      <figcaption className="absolute inset-x-0 bottom-0 p-4 md:p-5">
        <h3 className="text-base md:text-lg font-bold text-white drop-shadow">
          {shot.title}
        </h3>
        <p className="mt-1 max-w-xl text-xs md:text-sm leading-relaxed text-gray-200 drop-shadow">
          {shot.caption}
        </p>
      </figcaption>
    </figure>
  );
}

function AVConsulting() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <main
      className="flex-1 w-full px-4 md:px-8 py-12 max-w-6xl mx-auto"
      style={{ animation: isLoaded ? "fadeIn 0.6s ease-out" : undefined }}
    >
      {/* Hero */}
      <header
        className="relative overflow-hidden rounded-2xl border border-white/15 mb-12"
        style={{ animation: "cascadeIn 0.6s ease-out 0.1s backwards" }}
      >
        <img
          src="/assets/tiles/av-consulting.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "center 30%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/40" />
        <div className="relative z-10 px-6 md:px-10 py-16 md:py-24">
          <p
            className="font-plex-mono text-xs uppercase tracking-[0.18em] mb-3"
            style={{ color: ACCENT }}
          >
            AV &amp; Web-Conferencing Consulting
          </p>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white drop-shadow">
            The right room for every conversation.
          </h1>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-gray-200 leading-relaxed drop-shadow">
            I match each space to the web-conferencing and automation solution
            that actually fits how it's used — from huddle rooms to boardrooms,
            luxury residences to enterprise campuses. A decade-plus of design,
            programming, and integration.
          </p>
        </div>
      </header>

      {/* Approach / thesis */}
      <section
        className="mb-12"
        style={{ animation: "cascadeIn 0.6s ease-out 0.2s backwards" }}
      >
        <div className="rounded-2xl border border-white/10 bg-black/30 p-6 md:p-8">
          <h2
            className="font-plex-mono text-xs uppercase tracking-[0.16em] mb-3"
            style={{ color: ACCENT }}
          >
            How I Work
          </h2>
          <p className="text-lg md:text-xl text-gray-100 leading-relaxed max-w-3xl">
            The right room starts with the right question:{" "}
            <span className="text-white font-semibold">
              what actually happens in it?
            </span>{" "}
            A two-person huddle, a fifty-person town hall, and an executive
            briefing all call for different gear. My job is to recommend the
            product that fits the use case — not the biggest spec sheet — and
            then see it designed, programmed, and installed right.
          </p>
        </div>
      </section>

      {/* Luxury residential */}
      <section
        className="mb-14"
        style={{ animation: "cascadeIn 0.6s ease-out 0.3s backwards" }}
      >
        <h2
          className="font-plex-mono text-xs uppercase tracking-[0.16em] mb-1.5"
          style={{ color: ACCENT }}
        >
          Luxury Residential
        </h2>
        <p className="text-sm text-gray-400 mb-5 max-w-2xl">
          Private residences where the technology has to disappear — dedicated
          cinema, whole-home automation, and distributed AV across Beverly Hills
          estates, the California coast, and Vancouver. Powerful, and invisible.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {residentialGallery.map((shot) => (
            <GalleryFigure key={shot.src} shot={shot} />
          ))}
        </div>
      </section>

      {/* Featured engagement — Microsoft */}
      <section
        className="mb-14"
        style={{ animation: "cascadeIn 0.6s ease-out 0.4s backwards" }}
      >
        <h2
          className="font-plex-mono text-xs uppercase tracking-[0.16em] mb-5"
          style={{ color: ACCENT }}
        >
          Enterprise — Microsoft at Scale
        </h2>
        <div className="rounded-2xl border border-white/10 bg-black/30 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                Microsoft — meeting spaces at scale
              </h3>
              <p className="mt-1 text-sm" style={{ color: ACCENT }}>
                Owner's representative at Arup
              </p>
            </div>
          </div>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-gray-300 leading-relaxed">
            As an AV &amp; IT consultant at Arup, I served as Microsoft's owner's
            representative for web-conferencing rooms across their offices in the
            Western and Midwest US, Canada, and the EU. Every space had a
            different job to do — so each got the right platform for it: Surface
            Hubs, Skype &amp; Teams Room Systems, projection, and multi-site
            video, specified, programmed, and delivered end to end.
          </p>

          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {engagementStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
              >
                <p className="font-plex-mono text-[10px] uppercase tracking-[0.12em] text-gray-500">
                  {stat.label}
                </p>
                <p className="mt-1 text-sm md:text-base font-semibold text-white">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio gallery */}
      <section
        className="mb-14"
        style={{ animation: "cascadeIn 0.6s ease-out 0.4s backwards" }}
      >
        <h2
          className="font-plex-mono text-xs uppercase tracking-[0.16em] mb-1.5"
          style={{ color: ACCENT }}
        >
          A Tour of the Rooms
        </h2>
        <p className="text-sm text-gray-400 mb-5 max-w-2xl">
          Ten Microsoft spaces, each matched to a different solution. The variety
          is the point — the same client, ten different right answers.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {gallery.map((shot) => (
            <GalleryFigure key={shot.src} shot={shot} />
          ))}
        </div>
      </section>

      {/* Capabilities */}
      <section
        className="mb-14"
        style={{ animation: "cascadeIn 0.6s ease-out 0.5s backwards" }}
      >
        <h2
          className="font-plex-mono text-xs uppercase tracking-[0.16em] mb-4"
          style={{ color: ACCENT }}
        >
          What I Do
        </h2>
        <div className="flex flex-wrap gap-2">
          {capabilities.map((cap) => (
            <span
              key={cap}
              className="text-sm px-3 py-1.5 rounded-full border border-white/15 text-gray-200 bg-white/[0.03]"
            >
              {cap}
            </span>
          ))}
        </div>
      </section>

      {/* Selected experience */}
      <section style={{ animation: "cascadeIn 0.6s ease-out 0.6s backwards" }}>
        <h2
          className="font-plex-mono text-xs uppercase tracking-[0.16em] mb-5"
          style={{ color: ACCENT }}
        >
          Selected Experience
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {experience.map((exp) => (
            <article
              key={exp.company}
              className="rounded-xl border border-white/10 bg-black/30 p-5 transition-colors hover:border-white/20"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-xl font-bold text-white">{exp.company}</h3>
                <span className="font-plex-mono text-xs text-gray-400 shrink-0">
                  {exp.date}
                </span>
              </div>
              <p className="text-sm mt-0.5 font-medium" style={{ color: ACCENT }}>
                {exp.role}
                <span className="text-gray-400 font-normal"> · {exp.location}</span>
              </p>
              <p className="text-sm text-gray-300 mt-3 leading-relaxed">
                {exp.summary}
              </p>
              {exp.clients && (
                <div className="mt-4">
                  <p className="font-plex-mono text-[10px] uppercase tracking-[0.12em] text-gray-500 mb-1.5">
                    Clients
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {exp.clients.map((client) => (
                      <span
                        key={client}
                        className="text-[11px] px-2.5 py-1 rounded-full border border-white/15 text-gray-300 bg-white/[0.03]"
                      >
                        {client}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      <div className="mt-16 text-center text-gray-500 text-sm border-t border-white/10 pt-8">
        <p>© {new Date().getFullYear()} Walt Yao. All rights reserved.</p>
      </div>
    </main>
  );
}

export default AVConsulting;
